import {
  Autocomplete,
  TAutocompleteOption,
  TAutocompleteOptionGroup,
  TAutocompleteValue,
} from '@outdoorsyco/bonfire';
import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import {
  ILocationSearchAutocompleteListOption,
  useLocationSearchAutocompleteOptions,
} from '@/hooks/useLocationSearchAutocompleteOptions';
import { useLocationSearchSuggestions } from '@/hooks/useLocationSearchSuggestions';
import { formatDeliveryLocation } from '@/utility/formatDeliveryLocation';
import { itemizeAddress } from '@/utility/itemizeAddress';
import { getMapboxFeatureId } from '@/utility/mapbox';

type TAddressInputProps = {
  label?: string;
  inputValue?: string;
  onChange?: (inputValue: string) => void;
  onSelect?: (value: ILocationSearchAutocompleteListOption) => void;
  placesOption?: { location?: Array<number>; types?: string };
  error?: string;
  /** Renders a non-interactive, read-only field (no autocomplete suggestions). */
  disabled?: boolean;
  /** Helper text shown under the input (when there is no error). */
  helperText?: string;
};

export const AddressInput = ({
  label,
  inputValue = '',
  onChange,
  onSelect,
  placesOption,
  error,
  disabled,
  helperText,
}: TAddressInputProps) => {
  const intl = useIntl();

  // We don't really care to remember the user selection, but bonfire autocomplete
  // relies on an ID to know the selection happened and its time to close the menu.
  // We will add just some internal id that we will set on selection, and will be
  // the current timestamp as string.
  const [selectionId, setSelectionId] = useState<string>();

  const inputValueIsEmpty = !inputValue;

  // When the parent clears inputValue externally, clear the
  // selectionId so Autocomplete doesn't hold a stale selection state.
  useEffect(() => {
    if (inputValueIsEmpty) {
      setSelectionId(undefined);
    }
  }, [inputValueIsEmpty]);

  const { values, options } = useAddressInputOptions(
    // Skip suggestion fetching for read-only fields — there's nothing to pick.
    disabled ? '' : inputValue,
    placesOption?.location,
    placesOption?.types,
  );

  const handleChange = (value: TAutocompleteValue) => {
    if (value?.id) {
      const option = values[value.id];

      if (!option) return;

      setSelectionId(new Date().getTime().toString());
      onChange?.(formatDeliveryLocation(itemizeAddress(option.value)));
      onSelect?.(option);
    } else {
      setSelectionId(undefined);
      onChange?.(value?.text ?? '');
    }
  };

  const addressLabel =
    label ||
    intl.formatMessage({
      defaultMessage: 'Address',
      id: 'e6Ph5+',
    });

  return (
    <Autocomplete
      ariaLabel={addressLabel}
      label={addressLabel}
      inputValue={{ id: selectionId, text: inputValue }}
      onChange={handleChange}
      options={options}
      preFiltered
      offset={12}
      error={!!error}
      helperText={error || helperText}
      disabled={disabled}
      showClearButton={!disabled}
      overlayClassName="z-[1510]"
      sheetClassName="z-[1510]"
    />
  );
};

const useAddressInputOptions = (text: string, location?: number[], types?: string) => {
  const suggestions = useLocationSearchSuggestions(text, location, types);
  const { groupedOptions } = useLocationSearchAutocompleteOptions({
    shouldBuildGroupedOptions: true,
    includeCurrentLocation: false,
    suggestions,
  });

  return useMemo(() => {
    const values: Record<string, ILocationSearchAutocompleteListOption> = {};

    const options: TAutocompleteOptionGroup[] = groupedOptions.map(group => ({
      label: group.group,
      options: group.options.reduce<TAutocompleteOption[]>((acc, option) => {
        const id = getMapboxFeatureId(option.value);

        if (!id) return acc;

        // Keep a lookup from the option id back to the original option
        // so we can retrieve it when Autocomplete reports a selection.
        values[id] = option;

        acc.push({
          value: id,
          content: { label: option.label, description: option.sublabel },
          icon: option.icon ? <option.icon /> : undefined,
        });

        return acc;
      }, []),
    }));

    return { values, options };
  }, [groupedOptions]);
};
